import type { Entries } from './catalog';
import { actions } from './actions';
import { journeys } from './journeys';
import { credentials } from './credentials';
import { factors } from './factors';
import { invites } from './invites';
import { members } from './members';
import { home } from './home';
import { consent } from './consent';
import { connect } from './connect';
import { permissions } from './permissions';
import { outage } from './outage';

/**
 * English, the first-party source language.
 *
 * The voice is the family's: lead with the useful result or the next action, name an action and its
 * object, and describe what happened rather than how it feels. No hype, no forced reassurance, and no
 * claim that something is saved before it is.
 */
export const en: Entries = {
	'action.signout.everywhere': 'Sign out on all devices',
	'action.cancel': 'Cancel',
	show: 'Show',
	'action.save': 'Save',
	'action.saving': 'Saving…',
	'action.create': 'Create',
	'action.back': 'Back',
	'action.retry': 'Try again',
	'action.copy': 'Copy',
	'action.copied': 'Copied',
	'action.close': 'Close',

	'nav.overview': 'Overview',
	'nav.organizations': 'Organizations',
	'nav.account': 'Your account',
	'nav.security': 'Sign-in and security',
	'nav.activity': 'Security activity',
	'nav.tokens': 'Access tokens',
	'nav.skip': 'Skip to main content',
	'nav.group.account': 'Account',

	'return.action': 'Return to {product}',
	'switch.working': 'Switching person…',
	'switch.disabled': 'Switching people is available only in an enabled local development installation.',
	'switch.failed': 'That person could not be signed in.',
	'switch.retry': 'Try again',
	'quick.title': 'Quick login (local)',
	'quick.lead': 'Local development only. Choose a local person to sign in as them; whoever is signed in here is signed out first.',
	'quick.none': 'No organization yet',
	'quick.unanswered': 'The local people helper did not answer',
	'quick.restart': 'Start the local environment again.',
	'quick.back': 'Back to sign in',
	'quick.link': 'Quick login (local)',
	'leave.working': 'Signing out of Beyond…',
	'leave.done': 'You signed out of Beyond. Every product session that came from this sign-in has ended.',
	'leave.signin': 'Sign in again',
	'leave.failed': 'Signing out of Beyond did not complete, so every session is still open. Try again.',
	'leave.retry': 'Try again',

	'state.loading': 'Loading…',
	'state.failed': 'That was not done, and nothing changed. Try again.',
	'state.denied': 'You are not allowed to do that here. Nothing was changed.',
	'state.notfound': 'That is not here.',
	'state.expired': 'Your session expired. Sign in to continue.',
	'state.revoked': 'Your session ended. Sign in to continue.',
	'state.suspended': 'This account cannot sign in. Contact whoever administers it.',
	'state.unavailable': 'Beyond Accounts did not answer. Nothing was changed.',
	'state.unknown': 'The change may have been made. Beyond Accounts did not answer. Check before trying again.',
	'state.upstream': 'The identity provider is not answering right now. Try again in a moment.',
	'state.unsent': 'Beyond Accounts could not be reached. Nothing was changed.',
	'state.limited': 'Too many attempts. Wait {seconds} seconds and try again.',
	'state.retrying': 'Trying again…',
	'state.saved': 'Saved.',
	'state.savedStale': 'Saved. This page could not be refreshed, so what you see may be out of date.',

	'signin.title': 'Sign in',
	'signin.lead': 'One account for every Beyond product.',
	'signin.email': 'Email address',
	'signin.password': 'Password',
	'signin.submit': 'Sign in with email',
	'signin.with': 'Continue with {provider}',
	'signin.unavailable': 'Signing in is not available right now.',
	'signin.provider.unreachable': 'Signing in is not answering right now. Try again in a moment.',
	'signin.refused': 'Those details did not sign you in.',
	'signin.language': 'Language',
	'signin.returning': 'You will be returned to {product}.',
	'signin.working': 'Signing you in…',

	'account.title': 'Your account',
	'account.lead': 'Your name, language and appearance across Beyond.',
	'account.details': 'Your details',
	'account.name': 'Name',
	'account.name.help': 'What other members of your organizations see.',
	'account.preferences': 'Language and appearance for all of Beyond',
	'account.preferences.lead': 'Every Beyond product uses these.',
	'account.preferences.unsaved': 'This device uses your choice, but it was not saved to your account, so your other devices and products keep the old one. Choose again to retry.',
	'account.language': 'Language',
	'account.appearance': 'Appearance',
	'appearance.system': 'Follow the system',
	'appearance.light': 'Light',
	'appearance.dark': 'Dark',

	'identities.title': 'Ways to sign in',
	'identities.lead': 'Each of these signs you in to this account.',
	'identities.provider.github': 'GitHub',
	'identities.provider.google': 'Google',
	'identities.provider.email': 'Email',
	'identities.attach': 'Add {provider}',
	'signin.recover': 'Forgot your password?',
	'signin.recover.lead': 'Enter the address you sign in with. If it has an account here, a message is on its way.',
	'signin.recover.submit': 'Send a recovery message',
	'signin.recover.sent': 'If that address has an account here, a recovery message is on its way.',
	'identities.attach.lead': 'Add a sign-in method to this account. This does not create another account.',
	'identities.attached': 'That sign-in method is attached to this account.',
	'reset.title': 'Choose a new password',
	'reset.lead': 'This link works once. Setting a password signs you in.',
	'reset.password': 'New password',
	'reset.again': 'Repeat it',
	'reset.submit': 'Set the password',
	'reset.mismatch': 'The two do not match.',
	'reset.expired': 'That link is no longer valid. Ask for another one.',
	'confirm.title': 'Confirming your address',
	'confirm.lead': 'One moment while your account is finished.',
	'confirm.expired': 'That link is no longer valid.',
	'confirm.again': 'Go to sign in',
	'identities.detach.item': 'Remove…',
	'identities.linked': 'Added {date}',
	'identities.unverified': 'Address not confirmed',
	'identities.last': 'This is the only way into this account, so it cannot be removed. Add another first.',
	'identities.inuse': 'That identity already belongs to another account.',
	'identities.reauth': 'Sign in again to change how you sign in.',

	'legal.label': 'Legal',
	'legal.privacy': 'Privacy policy',
	'legal.terms': 'Terms of service',
	'legal.notice': 'By continuing you accept the terms of service and acknowledge the privacy policy.',

	'data.title': 'Your data',
	'data.lead': 'Take a copy of what Beyond Accounts holds about you, or delete your account.',
	'data.policy': 'How your data is handled',
	'data.copy': 'Download a copy',
	'data.copy.lead': 'A JSON file with your profile, sign-in methods, organizations, sessions and the records about your account. It leaves out credentials and other people’s details.',
	'data.copied': 'Your copy was downloaded.',
	'data.erase': 'Delete account',
	'data.erase.title': 'Delete this account',
	'data.erase.lead': 'Your account is deleted 14 days from now. Every session ends now; until then you can sign in and keep it.',
	'data.erase.word': 'DELETE',
	'data.erase.confirm': 'Type {word} to confirm',
	'data.erase.submit': 'Delete my account',
	'data.reauth': 'For your protection, this needs a recent sign-in. Sign in again, then come back here.',
	'data.reauth.action': 'Sign in again',
	'signin.erased': 'Your account will be deleted in 14 days. Sign in before then to keep it.',

	'organizations.title': 'Organizations',
	'organizations.lead': 'Your organizations and roles.',
	'organizations.none.title': 'You are not in an organization yet',
	'organizations.none.body': 'Create one, or accept an invitation someone sends you. You can use your account without one.',
	'organizations.create': 'Create organization',
	'organizations.create.title': 'Create an organization',
	'organizations.name': 'Name',
	'organizations.name.help': 'You can change it later.',
	'organizations.created': 'Organization created. You own it.',

	'settings.title': 'Settings',
	'settings.save.anyway': 'Save anyway',

	'role.owner': 'Owner',
	'role.admin': 'Administrator',
	'role.developer': 'Developer',
	'role.viewer': 'Viewer',
	'role.help.owner': 'Everything, including ownership itself.',
	'role.help.admin': 'Membership and invitations.',
	'role.help.developer': 'A member who works here.',
	'role.help.viewer': 'Reads.',
	'role.note': 'A role here decides membership. What someone may do in a product is decided by that product.',

	'team.title': 'Team',
	'members.title': 'Members',
	'members.count': { one: 'One member', other: '{count} members' },
	'members.unnamed': 'Unnamed',
	'members.role': 'Role',
	'members.removed': 'Removed',
	'members.remove.title': 'Remove {name}?',
	'members.remove.body': 'They lose their membership of {organization}.',
	'members.lastowner': 'An organization keeps at least one owner. Make someone else an owner first.',

	'invitations.title': 'Invitations',
	'invitations.lead': 'Each invitation belongs to one address, expires and works once.',
	'invitations.send': 'Invite member',
	'invitations.send.title': 'Invite someone',
	'invitations.email': 'Email address',
	'invitations.email.help': 'They must sign in with this address, confirmed by their provider, to accept.',
	'invitations.role': 'Role',
	'invitations.none': 'No invitations.',
	'invitations.pending': 'Waiting',
	'invitations.accepted': 'Accepted',
	'invitations.revoked': 'Withdrawn',
	'invitations.expires': 'Expires {date}',
	'invitations.revoke': 'Withdraw',
	'invitations.link': 'Invitation link',
	'invitations.link.once': 'This link is shown once and is not stored. Copy it now and send it to {email}.',
	'invitations.replaced': 'This replaces the invitation that was already open for this address.',
	'invitations.created': 'Invitation created. It waits until the link is used or expires.',

	'invitation.title': 'Join {organization}',
	'invitation.lead': 'You have been invited as {role}.',
	'invitation.accept': 'Accept invitation',
	'invitation.signin': 'Sign in to accept',
	'invitation.address': 'Sign in with {email} to accept this.',
	'invitation.unknown': 'That invitation is not valid.',
	'invitation.expired': 'That invitation has expired. Ask for a new one.',
	'invitation.closed': 'That invitation was already used or withdrawn.',
	'invitation.unverified': 'Sign in with the address this invitation was sent to, confirmed by your provider.',

	'products.title': 'Products',
	'products.lead': 'Open without signing in again.',

	'error.unexpected': 'That could not be done, and nothing was changed. Try again.',
	'error.network': 'Accounts could not be reached. Check your connection and try again.',
	'error.validation': 'Check what you entered.',
	'error.field.email': 'Enter a valid email address.',

	'language.en': 'English',
	'language.es': 'Español',

	'nav.notifications': 'Notifications',
	'notifications.title': 'Notifications',

	...actions.en,
	...journeys.en,
	...credentials.en,
	...factors.en,
	...invites.en,
	...members.en,
	...home.en,
	...consent.en,
	...connect.en,
	...permissions.en,
	...outage.en
};
